import { useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useLiveRefresh } from '../hooks/useLiveRefresh.js';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8009';

export function ProjectsView({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [refreshFlash, setRefreshFlash] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_URL}/api/projects/status`, { headers });
      setData(res.data);
      setError(null);
      setLastCheck(new Date());
      setRefreshFlash(true);
      setTimeout(() => setRefreshFlash(false), 800);
    } catch (err) {
      setError('Kon project status niet ophalen');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Één keer ophalen, daarna alleen op een signaal — `decisions/geen-polling.md`.
  // Dit scherm ververste elke 60 seconden.
  useLiveRefresh(token, fetchProjects);

  const sections = useMemo(() => {
    const entries = Object.entries(data?.projects || {});
    return {
      servers: entries.filter(([, p]) => (p.category || 'server') === 'server'),
      mobiles: entries.filter(([, p]) => p.category === 'mobile'),
    };
  }, [data]);

  if (loading && !data) {
    return <div style={styles.container}><p style={{ color: '#6b7280' }}>Laden...</p></div>;
  }

  const summary = data?.summary || { green: 0, orange: 0, red: 0 };
  const total = summary.green + summary.orange + summary.red;
  const worstStatus = summary.red > 0 ? 'red' : summary.orange > 0 ? 'orange' : 'green';

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Project Status</h2>
        <button
          onClick={fetchProjects}
          style={{
            ...styles.refreshBtn,
            transition: 'background 0.3s',
            background: refreshFlash ? '#d1fae5' : '#f3f4f6',
          }}
        >
          Ververs
        </button>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={{
        ...styles.summaryBar,
        background: STATUS_COLORS[worstStatus].bg,
        borderLeft: `4px solid ${STATUS_COLORS[worstStatus].border}`,
      }}>
        <span style={{ fontWeight: 600, color: STATUS_COLORS[worstStatus].text }}>
          {summary.green === total
            ? `Alle ${total} projecten OK`
            : `${summary.green}/${total} OK`}
        </span>
        {summary.orange > 0 && (
          <span style={{ color: '#92400e', marginLeft: 12 }}>
            {summary.orange} aandacht nodig
          </span>
        )}
        {summary.red > 0 && (
          <span style={{ color: '#991b1b', marginLeft: 12 }}>
            {summary.red} error
          </span>
        )}
      </div>

      {sections.servers.length > 0 && (
        <>
          <h3 style={styles.sectionHeader}>Server</h3>
          <div style={styles.cardList}>
            {sections.servers.map(([key, proj]) => (
              <ProjectCard key={key} project={proj} />
            ))}
          </div>
        </>
      )}
      {sections.mobiles.length > 0 && (
        <>
          <h3 style={styles.sectionHeader}>Mobile</h3>
          <div style={styles.cardList}>
            {sections.mobiles.map(([key, proj]) => (
              <ProjectCard key={key} project={proj} />
            ))}
          </div>
        </>
      )}

      {lastCheck && (
        <p style={styles.footer}>
          Laatste check: {lastCheck.toLocaleTimeString('nl-NL')} &middot; Auto-refresh: 60s
        </p>
      )}
    </div>
  );
}

function ProjectCard({ project }) {
  const statusColor = STATUS_COLORS[project.status] || STATUS_COLORS.red;
  const showMiddle = project.branch || project.lastCommit;

  return (
    <div style={{
      ...styles.card,
      borderLeft: `4px solid ${statusColor.border}`,
    }}>
      <div style={styles.cardTop}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: statusColor.border,
            flexShrink: 0,
          }} />
          <span style={{ fontWeight: 600, fontSize: 15, color: '#1f2937' }}>
            {project.name}
          </span>
        </div>
        {project.lastCommit?.relativeTime && (
          <span style={{ color: '#9ca3af', fontSize: 12 }}>
            {project.lastCommit.relativeTime}
          </span>
        )}
      </div>

      {showMiddle && (
        <div style={styles.cardMiddle}>
          {project.branch && <span style={styles.branchBadge}>{project.branch}</span>}
          {project.lastCommit && (
            <span style={{ color: '#6b7280', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: 12 }}>
                {project.lastCommit.shortHash}
              </span>
              {' '}{project.lastCommit.message}
            </span>
          )}
        </div>
      )}

      {project.openPRs > 0 && (
        <div style={{ ...styles.cardMiddle, color: '#6b7280', fontSize: 13 }}>
          {project.openPRs} open PR{project.openPRs === 1 ? '' : 's'}
        </div>
      )}

      {project.status !== 'green' && (
        <div style={{
          ...styles.statusReason,
          color: statusColor.text,
          background: statusColor.bg,
        }}>
          {project.statusLabel}
          {project.sync?.error && project.status !== 'red' && (
            <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>
              (sync: {project.sync.error})
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_COLORS = {
  green: { border: '#22c55e', bg: '#f0fdf4', text: '#166534' },
  orange: { border: '#f59e0b', bg: '#fffbeb', text: '#92400e' },
  red: { border: '#ef4444', bg: '#fef2f2', text: '#991b1b' },
};

const styles = {
  container: { padding: '20px', maxWidth: '640px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  title: { margin: 0, fontSize: '1.25rem', fontWeight: '700' },
  refreshBtn: { padding: '7px 14px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' },
  summaryBar: {
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  cardList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  sectionHeader: { margin: '12px 0 8px', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280', fontWeight: 600 },
  card: {
    background: 'white',
    borderRadius: '8px',
    padding: '12px 16px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardMiddle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 6,
    overflow: 'hidden',
  },
  branchBadge: {
    background: '#f3f4f6',
    color: '#4b5563',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: 12,
    fontFamily: 'monospace',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  statusReason: {
    marginTop: 8,
    padding: '6px 10px',
    borderRadius: '6px',
    fontSize: 13,
  },
  footer: { textAlign: 'center', color: '#9ca3af', fontSize: '0.8rem', marginTop: '12px' },
};
